import { useEffect, useState } from "react";
import { useDebouncedList } from "../../lib/useDebouncedList";
import { toast } from "sonner";
import { UserPlus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Field, btnGhost, btnPrimary, inputCls } from "../../components/ui";

const pill = "rounded-full px-2.5 py-0.5 text-xs font-semibold";

function ActionDialog({ action, roles, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", role: "support", reason: "" });
  useEffect(() => { if (action?.user) setF((x) => ({ ...x, role: action.user.role, reason: "" })); }, [action]);
  if (!action) return null;
  const { kind, user } = action;
  const assignable = roles.filter((r) => r.assignable);
  const titles = { create: "Criar utilizador", role: `Alterar perfil · ${user?.name}`, status: `${user?.is_active === false ? "Reativar" : "Suspender"} · ${user?.name}`, mfa: `Repor MFA · ${user?.name}` };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (kind === "create") await api.post("/admin/users", f);
      if (kind === "role") await api.patch(`/admin/users/${user.id}`, { role: f.role, reason: f.reason });
      if (kind === "status") await api.post(`/admin/users/${user.id}/status`, { active: user.is_active === false, reason: f.reason });
      if (kind === "mfa") await api.post(`/admin/users/${user.id}/mfa-reset`, { reason: f.reason });
      toast.success(kind === "create" ? "Utilizador criado. Convite enviado por email" : "Alteração registada");
      onDone();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-action-dialog">
        <DialogHeader><DialogTitle>{titles[kind]}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          {kind === "create" && <>
            <Field label="Nome"><input required minLength={2} value={f.name} onChange={set("name")} className={inputCls} data-testid="user-form-name" /></Field>
            <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="user-form-email" /></Field>
          </>}
          {(kind === "create" || kind === "role") &&
            <Field label="Perfil"><select value={f.role} onChange={set("role")} className={inputCls} data-testid="user-form-role">{assignable.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></Field>}
          {kind === "mfa" && <p className="text-sm text-gray-600">O utilizador terá de configurar novamente a aplicação autenticadora no próximo acesso.</p>}
          <Field label="Motivo (fica registado na auditoria)"><textarea required minLength={3} maxLength={300} rows={2} value={f.reason} onChange={set("reason")} className={inputCls} data-testid="user-form-reason" /></Field>
          <button className={btnPrimary} data-testid="user-form-submit">Confirmar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, label, canManage, me, onAction }) {
  const active = u.is_active !== false;
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 text-sm" data-testid={`admin-user-${u.id}`}>
      <div className="min-w-[200px] flex-1"><p className="font-medium">{u.name}</p><p className="text-xs text-gray-500">{u.email} · {fmtDate(u.created_at)}</p></div>
      <span className={`${pill} bg-gray-100`} data-testid={`admin-user-role-${u.id}`}>{label}</span>
      <span className={`${pill} ${active ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`} data-testid={`admin-user-status-${u.id}`}>{active ? "Ativa" : "Suspensa"}</span>
      {u.mfa_enabled && <span className={`${pill} bg-bo-green/10 text-bo-green`}>MFA</span>}
      {!u.email_verified && <span className={`${pill} bg-amber-100 text-amber-800`}>Por confirmar</span>}
      {canManage && u.id !== me && <div className="flex gap-1">
        {!["store", "courier"].includes(u.role) && <button onClick={() => onAction("role", u)} className={btnGhost} data-testid={`admin-user-role-btn-${u.id}`}>Perfil</button>}
        <button onClick={() => onAction("status", u)} className={btnGhost} data-testid={`admin-user-status-btn-${u.id}`}>{active ? "Suspender" : "Reativar"}</button>
        {u.mfa_enabled && <button onClick={() => onAction("mfa", u)} className={btnGhost} data-testid={`admin-user-mfa-btn-${u.id}`}>Repor MFA</button>}
      </div>}
    </div>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const canManage = user?.permissions?.includes("users.manage");
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({ q: "", role: "", status: "", page: 1 });
  const [data, load] = useDebouncedList("/admin/users", filters, { items: [], total: 0, limit: 25 });
  const [action, setAction] = useState(null);
  useEffect(() => { api.get("/admin/roles").then(({ data: d }) => setRoles(d.roles)); }, []);
  const label = Object.fromEntries(roles.map((r) => [r.key, r.label]));
  const pages = Math.max(1, Math.ceil(data.total / data.limit));
  const setFilter = (k) => (e) => setFilters({ ...filters, [k]: e.target.value, page: 1 });
  return (
    <div className="flex flex-col gap-3" data-testid="admin-users">
      <div className="bo-card flex flex-wrap items-end gap-3 p-4">
        <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input placeholder="Pesquisar nome ou email" value={filters.q} onChange={setFilter("q")} className={`${inputCls} pl-9`} data-testid="admin-users-search" /></div>
        <select value={filters.role} onChange={setFilter("role")} className={`${inputCls} w-auto`} data-testid="admin-users-role-filter"><option value="">Todos os perfis</option>{roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select>
        <select value={filters.status} onChange={setFilter("status")} className={`${inputCls} w-auto`} data-testid="admin-users-status-filter"><option value="">Todos os estados</option><option value="active">Ativas</option><option value="suspended">Suspensas</option></select>
        {canManage && <button onClick={() => setAction({ kind: "create" })} className={btnPrimary} data-testid="admin-users-create-btn"><UserPlus className="h-4 w-4" />Criar utilizador</button>}
      </div>
      <div className="bo-card divide-y divide-gray-50" data-testid="admin-users-list">
        {data.items.map((u) => <UserRow key={u.id} u={u} label={label[u.role] || u.role} canManage={canManage} me={user.id} onAction={(kind, x) => setAction({ kind, user: x })} />)}
        {!data.items.length && <p className="p-4 text-sm text-gray-500">Sem resultados.</p>}
      </div>
      <div className="flex items-center justify-end gap-2 text-sm" data-testid="admin-users-pagination">
        <span className="text-gray-500">{data.total} utilizadores · página {filters.page} de {pages}</span>
        <button disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })} className={btnGhost} data-testid="admin-users-prev"><ChevronLeft className="h-4 w-4" /></button>
        <button disabled={filters.page >= pages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })} className={btnGhost} data-testid="admin-users-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <ActionDialog action={action} roles={roles} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}
